import { supabase } from '@/lib/supabase'
import type {
  HangarUnitRow,
  HangarUnitInsert,
  HangarUnitUpdate,
} from '@/types/hangar-units'

/**
 * Fetch all units for a building
 */
export async function fetchHangarUnits(buildingId: string): Promise<HangarUnitRow[]> {
  const { data, error } = await supabase
    .from('units')
    .select('*')
    .eq('building_id', buildingId)
    .is('deleted_at', null)
    .order('sequence')

  if (error) throw error
  return (data as HangarUnitRow[]) || []
}

/**
 * Fetch all units for an account (across all buildings)
 */
export async function fetchAllUnitsForAccount(organizationId: string): Promise<HangarUnitRow[]> {
  const { data, error } = await supabase
    .from('units')
    .select('*, buildings(name)')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('unit_code')

  if (error) throw error
  return (data as HangarUnitRow[]) || []
}

/**
 * Fetch a single unit by ID
 */
export async function fetchHangarUnit(id: string): Promise<HangarUnitRow> {
  const { data, error } = await supabase
    .from('units')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as HangarUnitRow
}

/**
 * Create a new unit
 */
export async function createHangarUnit(data: HangarUnitInsert): Promise<HangarUnitRow> {
  const { data: created, error } = await supabase
    .from('units')
    .insert([{
      building_id: data.building_id,
      organization_id: data.organization_id,
      unit_code: data.unit_code.toUpperCase(),
      sequence: data.sequence,
      door_width: data.door_width ?? null,
      depth: data.depth ?? null,
      tail_width: data.tail_width ?? null,
      offset_x: data.offset_x ?? 0,
      offset_y: data.offset_y ?? 0,
      is_leasable: data.is_leasable ?? true,
      status: data.status ?? 'available',
      pricing_override: data.pricing_override ?? false,
      unit_monthly_rate: data.unit_monthly_rate ?? null,
      unit_rate_per_sqft: data.unit_rate_per_sqft ?? null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any])
    .select()
    .single()

  if (error) throw error
  return created as HangarUnitRow
}

/**
 * Create multiple units at once (batch insert)
 */
export async function createHangarUnits(
  units: HangarUnitInsert[]
): Promise<HangarUnitRow[]> {
  const insertData = units.map((unit) => ({
    building_id: unit.building_id,
    organization_id: unit.organization_id,
    unit_code: unit.unit_code.toUpperCase(),
    sequence: unit.sequence,
    door_width: unit.door_width ?? null,
    depth: unit.depth ?? null,
    tail_width: unit.tail_width ?? null,
    offset_x: unit.offset_x ?? 0,
    offset_y: unit.offset_y ?? 0,
    is_leasable: unit.is_leasable ?? true,
    status: unit.status ?? 'available',
    pricing_override: unit.pricing_override ?? false,
    unit_monthly_rate: unit.unit_monthly_rate ?? null,
    unit_rate_per_sqft: unit.unit_rate_per_sqft ?? null,
  }))

  const { data: created, error } = await supabase
    .from('units')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .insert(insertData as any)
    .select()

  if (error) throw error
  return (created as HangarUnitRow[]) || []
}

/**
 * Update an existing hangar unit
 */
export async function updateHangarUnit(
  id: string,
  data: HangarUnitUpdate
): Promise<HangarUnitRow> {
  const updateData: Record<string, unknown> = {}

  if (data.unit_code !== undefined) updateData.unit_code = data.unit_code.toUpperCase()
  if (data.sequence !== undefined) updateData.sequence = data.sequence
  if (data.door_width !== undefined) updateData.door_width = data.door_width
  if (data.depth !== undefined) updateData.depth = data.depth
  if (data.tail_width !== undefined) updateData.tail_width = data.tail_width
  if (data.offset_x !== undefined) updateData.offset_x = data.offset_x
  if (data.offset_y !== undefined) updateData.offset_y = data.offset_y
  if (data.is_leasable !== undefined) updateData.is_leasable = data.is_leasable
  if (data.status !== undefined) updateData.status = data.status
  if (data.pricing_override !== undefined)
    updateData.pricing_override = data.pricing_override
  if (data.unit_monthly_rate !== undefined)
    updateData.unit_monthly_rate = data.unit_monthly_rate
  if (data.unit_rate_per_sqft !== undefined)
    updateData.unit_rate_per_sqft = data.unit_rate_per_sqft

  const { data: updated, error } = await supabase
    .from('units')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return updated as HangarUnitRow
}

/**
 * Soft delete a hangar unit (sets deleted_at timestamp)
 */
export async function deleteHangarUnit(id: string): Promise<void> {
  const { error } = await supabase
    .from('units')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Soft delete all units for a building
 * Useful when regenerating units from template
 */
export async function deleteAllHangarUnits(buildingId: string): Promise<void> {
  const { error } = await supabase
    .from('units')
    .update({ deleted_at: new Date().toISOString() })
    .eq('building_id', buildingId)
    .is('deleted_at', null)

  if (error) throw error
}
